import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Copy, CheckCircle2, ExternalLink, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ConnectDataIcon } from "../icons/WizardModeIcons";
import {
  buildPriceIds,
  clientUsesBookings,
  isEarlySupporter,
  isTrialStart,
  planSoldOnCycle,
  planSummary,
} from "../pricing";
import { invokeInternal } from "../invoke";
import { formatTrialDate, trialEndDate } from "../trials";
import { isStripeTestMode } from "@/config/stripePlans";
import type { OnboardingData, StepProps } from "../types";

const PAID_POLL_MS = 6000;

// Shown to the operator after an AI-only client is set up: the agent runs in
// answer-only mode until the client's own data/actions are connected.
const AiOnlyNextStep = () => (
  <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-left">
    <div className="flex items-start gap-3">
      <ConnectDataIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="text-sm">
        <div className="font-semibold">Next: connect their data &amp; actions</div>
        <p className="text-xs text-muted-foreground mt-0.5">
          This is an AI-assistant-only client (no Dibs Bookings). In the client's
          dashboard, go to <span className="font-medium">Business data → Connections</span>{" "}
          to hook up their live data (URL feed / API), write actions, or an MCP
          server — so the AI can answer and act for them. Until then the agent
          answers from the business info you entered and hands off transactions.
        </p>
      </div>
    </div>
  </div>
);

const onboardingMetadata = (data: OnboardingData) => ({
  businessName: data.businessName,
  businessEmail: data.clientEmail,
  businessAddress: data.businessAddress,
  category: data.category,
  phone: data.phone,
  contactName: data.contactName,
  selectedPlan: data.selectedPlan,
  billingCycle: data.billingCycle,
  isYearly: data.billingCycle === "yearly" ? "true" : "false",
  isEarlySupporter: isEarlySupporter(data) ? "true" : "false",
  earlySupporterGrants: data.earlySupporterGrants.join(","),
});

const FinishStep = ({ data }: StepProps) => {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [paid, setPaid] = useState(false);
  const [freeOnboarded, setFreeOnboarded] = useState(false);
  const [trialStarted, setTrialStarted] = useState(false);
  const [copied, setCopied] = useState(false);

  const clientEmail = data.clientEmail.toLowerCase().trim();
  const trialStart = isTrialStart(data);

  const warnIfNoSupportAccess = (supportAccess?: string) => {
    if (supportAccess === "failed") {
      toast.warning("Dibs support access could not be added. Try again from this step.");
    }
  };

  const sendPaymentLink = async () => {
    setLoading(true);
    try {
      const { data: res, error } = await invokeInternal<{
        url?: string;
        free?: boolean;
        email_sent?: boolean;
        support_access?: string;
        error?: string;
      }>("internal-onboarding-checkout", {
        email: clientEmail,
        priceIds: buildPriceIds(data),
        test_mode: isStripeTestMode,
        origin: window.location.origin,
        summary: planSummary(data),
        metadata: onboardingMetadata(data),
      });
      if (error) throw new Error(error.message);
      if (res?.error) throw new Error(res.error);
      warnIfNoSupportAccess(res?.support_access);
      if (res?.free) {
        setFreeOnboarded(true);
        toast.success(
          isEarlySupporter(data)
            ? "Client onboarded on the Early Supporter Plan"
            : "Client onboarded on Free plan"
        );
        return;
      }
      if (!res?.url) throw new Error("No payment link returned");
      setUrl(res.url);
      setEmailSent(res.email_sent === true);
      if (res.email_sent) {
        toast.success(`Payment link sent to ${clientEmail}`);
      } else {
        toast.warning("The email didn't go out. Copy the Stripe link and send it yourself.");
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send the payment link");
    } finally {
      setLoading(false);
    }
  };

  const startTrial = async () => {
    setLoading(true);
    try {
      const { data: res, error } = await invokeInternal<{
        success?: boolean;
        support_access?: string;
        error?: string;
      }>("internal-trial-accounts", {
        action: "set",
        email: clientEmail,
        plan: data.selectedPlan,
        days: data.trialDays,
        never_expires: data.trialNeverExpires,
        include_bookings: clientUsesBookings(data),
        metadata: onboardingMetadata(data),
      });
      if (error) throw new Error(error.message);
      if (!res?.success) throw new Error(res?.error || "Failed to start the trial");
      warnIfNoSupportAccess(res.support_access);
      setTrialStarted(true);
      toast.success("Trial started");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to start the trial");
    } finally {
      setLoading(false);
    }
  };

  const checkPaid = useCallback(async () => {
    const { data: state, error } = await invokeInternal<{
      subscription_status?: string | null;
    }>("internal-business-state", { clientEmail });
    if (!error && state?.subscription_status === "active") setPaid(true);
  }, [clientEmail]);

  useEffect(() => {
    if (!url || paid) return;
    const timer = window.setInterval(() => void checkPaid(), PAID_POLL_MS);
    return () => window.clearInterval(timer);
  }, [url, paid, checkPaid]);

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (freeOnboarded) {
    return (
      <div className="space-y-6 text-center py-6">
        <div className="flex flex-col items-center gap-2">
          <CheckCircle2 className="h-12 w-12 text-primary" />
          <h2 className="text-2xl font-heading font-semibold">
            Client onboarded
          </h2>
          <p className="text-sm text-muted-foreground">
            {data.clientEmail} is now set up
            {isEarlySupporter(data) ? " on the Early Supporter Plan" : " on the Free plan"}.
            They can sign in to the admin dashboard at any time.
          </p>
        </div>
        {!clientUsesBookings(data) && <AiOnlyNextStep />}
      </div>
    );
  }

  if (trialStarted) {
    return (
      <div className="space-y-6 text-center py-6">
        <div className="flex flex-col items-center gap-2">
          <CheckCircle2 className="h-12 w-12 text-primary" />
          <h2 className="text-2xl font-heading font-semibold">Trial started</h2>
          <p className="text-sm text-muted-foreground">
            {data.clientEmail} can use their dashboard{" "}
            {data.trialNeverExpires
              ? "with no end date"
              : `until ${formatTrialDate(trialEndDate(data.trialDays))}`}
            . You can change this any time from Trial accounts.
          </p>
        </div>
        {!clientUsesBookings(data) && <AiOnlyNextStep />}
      </div>
    );
  }

  if (trialStart) {
    return (
      <div className="space-y-6 text-center py-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-heading font-semibold">Start the trial</h2>
          <p className="text-sm text-muted-foreground">
            This saves the onboarding details to {data.clientEmail}'s account and
            opens their dashboard{" "}
            {data.trialNeverExpires
              ? "with no end date"
              : `until ${formatTrialDate(trialEndDate(data.trialDays))}`}
            . No payment needed.
          </p>
        </div>
        <Button size="lg" onClick={startTrial} disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Start trial
        </Button>
      </div>
    );
  }

  if (!url) {
    // No Stripe line items (Free plan, or comped Early Supporter grants) → the
    // no-payment completion path.
    // planSoldOnCycle first: buildPriceIds throws for a plan not sold on the cycle.
    const isFree = planSoldOnCycle(data) && buildPriceIds(data).length === 0;
    return (
      <div className="space-y-6 text-center py-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-heading font-semibold">
            {isFree ? "Complete onboarding" : "Send payment link"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isFree
              ? `This saves the onboarding details to ${data.clientEmail}'s account and activates the Free plan. No payment needed.`
              : `This saves the onboarding details to ${data.clientEmail}'s account and emails them a link to pay for ${planSummary(data)}.`}
          </p>
        </div>
        <Button size="lg" onClick={sendPaymentLink} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : !isFree && <Mail className="h-4 w-4" />}
          {isFree ? "Complete Free onboarding" : "Send payment link"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 text-center py-4">
      <div className="flex flex-col items-center gap-2">
        <CheckCircle2 className="h-12 w-12 text-primary" />
        <h2 className="text-2xl font-heading font-semibold">
          {paid ? "Payment received" : emailSent ? "Payment link sent" : "Payment link ready"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {paid
            ? `${data.clientEmail} has paid and their dashboard is open.`
            : emailSent
              ? `We emailed ${data.clientEmail} a link to pay for ${planSummary(data)}. Their dashboard opens as soon as they pay.`
              : `The email didn't go out, so send ${data.clientEmail} the Stripe link below.`}
        </p>
      </div>

      {!paid && (
        <>
          <Button variant="outline" size="sm" onClick={sendPaymentLink} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Resend email
          </Button>

          <details className="rounded-xl border p-4 text-left" open={!emailSent}>
            <summary className="cursor-pointer text-sm font-medium">
              On a call? Use the Stripe link or QR code
            </summary>
            <div className="mt-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                This goes straight to Stripe and lasts 24 hours. The emailed link doesn't expire.
              </p>
              <div className="flex justify-center">
                <div className="rounded-xl border bg-card p-4">
                  <QRCodeSVG value={url} size={180} />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 items-center justify-center">
                <code className="text-xs bg-muted px-3 py-2 rounded-md max-w-full truncate">
                  {url}
                </code>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copy}>
                    {copied ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    Copy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(url, "_blank")}
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open
                  </Button>
                </div>
              </div>
            </div>
          </details>
        </>
      )}

      {!clientUsesBookings(data) && <AiOnlyNextStep />}
    </div>
  );
};

export default FinishStep;
